import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { DirectBookingSitesService, WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';

import { WebsiteDomainSettingsComponent } from '../website-domain-settings/website-domain-settings.component';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { environment } from 'src/environments/environment';

const BASE_URL = environment.pmsites_url;

@Component({
  selector: 'website-tab',
  templateUrl: './website-tab.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WebsiteTabComponent implements OnInit, OnDestroy {
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly navCtrl = inject(CustomNavController);
  private readonly client = inject(Client);
  private readonly directBookingSites = inject(DirectBookingSitesService);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoBlock = inject(DemoBlockService);

  readonly windowService = inject(WindowService);

  configs: WebsiteConfig[] = [];
  headers: string[] = [
    'Property',
    'Site URL',
    'Custom Domain',
    'Status',
    'last-column',
  ];
  allRows: any[] = [];
  rows: any[] = [];
  isDataLoaded = false;
  errorMessage = '';
  showAboutThisPage = false;
  overlayTop: string;
  searchForm = this.fb.group({ search: [''] });

  // The address whose detail page is open in the right side panel, so its row
  // can be refreshed when the panel closes.
  private lastOpenedAddressId: number | null = null;

  private _websiteHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;

  @ViewChild('tidyTable') tidyTable: any;
  @ViewChild('websiteHeader', { read: ElementRef }) set websiteHeader(el: ElementRef) {
    this._websiteHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  async ngOnInit() {
    this.searchForm.get('search')!.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.filterRows(value || '');
      this.cdr.markForCheck();
    });

    // Re-read the address the detail page was showing once the panel closes, so
    // a custom-domain change there is reflected in the row instead of going stale.
    // Reads the server rather than dialog-params: closeRightSidePanel() deletes
    // that key in the same tick it flips isOpen$, so the read would race it.
    this.rightSidePanelService.afterPanelClosed().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => void this.refreshOpenedConfig());

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('direct-booking');
    if (this.showAboutThisPage) {
      this.loadMockData();
      return;
    }

    await this.loadRealData();
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
  }

  private async refreshOpenedConfig(): Promise<void> {
    const addressId = this.lastOpenedAddressId;
    this.lastOpenedAddressId = null;
    if (this.showAboutThisPage || addressId === null) {return;}
    const index = this.configs.findIndex(config => config.id === addressId);
    if (index === -1) {return;}
    this.configs[index] = await this.directBookingSites.refreshConfig(this.configs[index]);
    this.buildRows(this.configs);
    this.filterRows(this.searchForm.get('search')!.value || '');
    this.cdr.markForCheck();
  }

  private loadMockData() {
    this.configs = this.getMockConfigs();
    this.buildHeaders();
    this.buildRows(this.configs);
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private async loadRealData() {
    this.buildHeaders();
    try {
      const addresses = await this.client.getAddresses();
      this.configs = await this.directBookingSites.buildConfigs(addresses || []);
      this.buildRows(this.configs);
    } catch (err: any) {
      this.errorMessage = err?.error?.message || err?.message || '';
    }
    this.isDataLoaded = true;
    this.cdr.markForCheck();
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._websiteHeader) {return;}
      const top = this._websiteHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top}px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('direct-booking');
    this.configs = [];
    this.rows = [];
    this.allRows = [];
    this.cdr.markForCheck();
  }

  buildHeaders(): void {
    this.headers = [
      'Property',
      'Site URL',
      'Custom Domain',
      'Status',
      'last-column',
    ];
  }

  buildRows(configs: WebsiteConfig[]): void {
    this.allRows = configs.map(config => ({
      id: config.id,
      'Property': {
        value: config.property_name,
        action: null,
        // Renders tidy-tag — the per-address colored home glyph, matching the
        // schedule weekly view and the channels table.
        color: config.color,
        addressId: config.id,
      },
      'Site URL': {
        value: config.site_url,
        // A paused site's URL serves the unavailable page — nothing to open.
        action: config.is_published ? () => this.util.openUrl(config.site_url) : null,
      },
      'Custom Domain': {
        value: config.custom_domain || 'Not configured',
        action: null,
      },
      'Status': {
        value: this.statusCellHtml(config),
        action: null,
      },
      'last-column': {
        value: config.id,
        action: null,
      },
    }));
    this.rows = [...this.allRows];
  }

  // Colored dot + label, matching the Status cells elsewhere in the app. A cell
  // `color` field would render tidy-tag (a home glyph) instead, which is wrong here.
  private statusCellHtml(config: WebsiteConfig): string {
    const color = config.is_published ? 'var(--tidy-success, #41CAB7)' : '#8E8E93';
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;` +
      `background-color:${color};margin-right:6px;vertical-align:middle;"></span>` +
      `${this.statusLabel(config)}`;
  }

  // Listed/Paused, matching the direct-booking dropdowns and the listings table.
  statusLabel(config: WebsiteConfig): string {
    return config.is_published ? 'Listed' : 'Paused';
  }

  filterRows(searchText: string): void {
    if (!searchText) {
      this.rows = [...this.allRows];
      return;
    }
    const lower = searchText.toLowerCase();
    // Match Status on its plain label — the cell value carries the dot markup.
    this.rows = this.allRows.filter(row => {
      const config = this.configs.find(c => c.id === row.id);
      return row['Property']?.value?.toLowerCase().includes(lower) ||
        row['Custom Domain']?.value?.toLowerCase().includes(lower) ||
        (config ? this.statusLabel(config).toLowerCase().includes(lower) : false);
    });
  }

  onTableRowClick(event: { column: string; element: any }): void {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) { return; }
    const config = this.configs.find(c => c.id === event.element.id);
    if (!config) {return;}
    this.openDomainSettings(config);
  }

  // Opens the live site in a new tab — the custom domain when one is set
  // (stored bare, e.g. "book.example.com"), otherwise the default site URL.
  getViewAction(config: WebsiteConfig): () => void {
    return () => this.util.openUrl(
      config.custom_domain ? `https://${config.custom_domain}` : config.site_url
    );
  }

  getDomainAction(config: WebsiteConfig): () => void {
    return () => this.openDomainSettings(config);
  }

  openDomainSettings(config: WebsiteConfig): void {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) {return;}
    const params = { websiteConfig: config };
    this.storage.save('dialog-params', params);
    this.lastOpenedAddressId = config.id;

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(WebsiteDomainSettingsComponent);
    } else {
      this.navCtrl.navigateForward('website-domain-settings', params);
    }
  }

  private getMockConfigs(): WebsiteConfig[] {
    return [
      {
        id: 1,
        property_name: 'Oceanview Beach House',
        property_address: '123 Coastal Drive, Malibu, CA 90265',
        site_url: `${BASE_URL}/s/site-1`,
        editor_url: `${BASE_URL}/editor?token=site-1`,
        custom_domain: 'book.oceanviewbeach.com',
        is_published: true,
        color: '#3A7DBA',
      },
      {
        id: 2,
        property_name: 'Mountain Retreat Cabin',
        property_address: '456 Pine Ridge Road, Big Bear Lake, CA 92315',
        site_url: `${BASE_URL}/s/site-2`,
        editor_url: `${BASE_URL}/editor?token=site-2`,
        custom_domain: '',
        is_published: true,
        color: '#41924B',
      },
      {
        id: 3,
        property_name: 'Downtown Loft',
        property_address: '789 Main Street, Unit 4B, Los Angeles, CA 90012',
        site_url: `${BASE_URL}/s/site-3`,
        editor_url: `${BASE_URL}/editor?token=site-3`,
        custom_domain: '',
        is_published: false,
        color: '#C9973E',
      },
    ];
  }
}
